import { Button, Card, Empty, List, Popconfirm, Space, Tag, Typography } from "antd";
import dayjs from "dayjs";
import 'dayjs/locale/de';
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import type { VolunteerSlot, VolunteerTask } from "../types";
import type { VolunteerStore } from "../store";
import { SignupModal } from "./SignupModal";
import { SlotFormModal } from "./SlotFormModal";
import { EmailContactsModal } from "./EmailContactsModal";
import { StackRow, wrapText } from "./responsive";

interface Props {
  store: VolunteerStore;
  task: VolunteerTask;
  slot: VolunteerSlot;
  isAdmin: boolean;
  taskTag?: string;
}

function formatSlotLabel(slot: VolunteerSlot): string {
  if (slot.label) return slot.label;
  return formatSlotTime(slot);
}

function formatSlotTime(slot: VolunteerSlot): string {
  dayjs.locale("de");
  if (slot.startTime && slot.endTime) {
    const sameDay = dayjs(slot.startTime).isSame(dayjs(slot.endTime), "day");
    return sameDay
      ? `${dayjs(slot.startTime).format("ddd HH:mm")} – ${dayjs(slot.endTime).format("HH:mm")} Uhr`
      : `${dayjs(slot.startTime).format("ddd HH:mm")} – ${dayjs(slot.endTime).format("ddd HH:mm")} Uhr`;
  }
  if (slot.startTime) return "- " + dayjs(slot.startTime).format("ddd HH:mm") + " Uhr";
  return ""
}

export const SlotCard = observer(({ store, task, slot, isAdmin, taskTag }: Props) => {
  const [expanded, setExpanded] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  const signups = store.signupsForSlot(slot.objectId!);
  const full = store.isSlotFull(slot.objectId!);

  const stop = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    fn();
  };

  return (
    <Card size="small" style={{ marginBottom: 12 }}>
      <StackRow
        $align="flex-start"
        style={{ cursor: "pointer" }}
        onClick={() => setExpanded(!expanded)}
      >
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
          <Typography.Text style={{ marginRight: 8, display: "inline-block" }}>{expanded ? "▼" : "▶"}</Typography.Text>
          {taskTag && <Tag style={{ marginRight: 8, ...wrapText }}>{taskTag}</Tag>}
          <Typography.Text strong style={{marginRight: 8, ...wrapText}}>{formatSlotLabel(slot)}</Typography.Text>
          {slot.startTime && <Typography.Text type="secondary" style={wrapText}> {formatSlotTime(slot)}</Typography.Text>}
          {slot.capacity ? (
            <Tag color={full ? "red" : "green"} style={{ marginLeft: 8 }}>
              {signups.length} / {slot.capacity}
            </Tag>
          ) : (
            <Tag style={{ marginLeft: 8 }}>{signups.length} angemeldet</Tag>
          )}
        </div>
        <Space wrap>
          <Button type="primary" size="small" disabled={full} onClick={stop(() => setSignupOpen(true))}>
            Eintragen
          </Button>
          {isAdmin && (
            <>
              <Button size="small" onClick={stop(() => setEmailOpen(true))}>
                Kontakte senden
              </Button>
              <Button size="small" onClick={stop(() => setEditOpen(true))}>
                Bearbeiten
              </Button>
            </>
          )}
        </Space>
      </StackRow>

      {expanded && (
        <>
          {slot.description && (
            <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
              {slot.description}
            </Typography.Paragraph>
          )}

          {(task.contactPersonName || (isAdmin && task.contactPersonPhone)) && (
            <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
              {task.contactPersonName && <>Ansprechperson für Thema "{taskTag}": {task.contactPersonName}</>}
              {isAdmin && task.contactPersonPhone && (
                <>
                  {task.contactPersonName && " · "}
                  Tel: {task.contactPersonPhone}
                </>
              )}
            </Typography.Paragraph>
          )}

          {signups.length === 0 ? (
            <Empty description="Noch niemand eingetragen" style={{ margin: "12px 0" }} />
          ) : (
            <List
              size="small"
              style={{ marginTop: 8 }}
              dataSource={signups}
              renderItem={(signup) => (
                <List.Item
                  actions={[
                    <Popconfirm
                      key="delete"
                      title="Anmeldung wirklich löschen?"
                      onConfirm={() => store.deleteSignup(signup.objectId!)}
                      okText="Ja"
                      cancelText="Nein"
                    >
                      <Button size="small" danger>
                        Löschen
                      </Button>
                    </Popconfirm>,
                  ]}
                >
                  <List.Item.Meta
                    title={`${signup.firstName} ${signup.lastNameAbbrev}`}
                    description={task.notesTitle && signup.note ? `${task.notesTitle}: ${signup.note}` : undefined}
                  />
                </List.Item>
              )}
            />
          )}
        </>
      )}

      <SignupModal
        store={store}
        task={task}
        slot={slot}
        open={signupOpen}
        onClose={() => setSignupOpen(false)}
      />
      {isAdmin && (
        <>
          <SlotFormModal
            store={store}
            task={task}
            slot={slot}
            open={editOpen}
            onClose={() => setEditOpen(false)}
          />
          <EmailContactsModal
            store={store}
            target={{ slotId: slot.objectId }}
            title={`${task.title} – ${formatSlotTime(slot)}`}
            open={emailOpen}
            onClose={() => setEmailOpen(false)}
          />
        </>
      )}
    </Card>
  );
});
